import { useMemo } from "react";
import { router } from "expo-router";
import { MatchCase, plural, Swiper, Task } from "@ptsd/uikit";
import i18next from "i18next";
import { useMedia, View, XStack } from "tamagui";

import { api } from "~/utils/api";

interface TaskData {
  path: string;
  title: string;
  image: string;
  description: string;
  buttonText: string;
  timeDescription: string | undefined;
  disabled: boolean | undefined;
}

//TODO: make it better
const getTasks = ({
  todo,
  isDiagnosticDelayed,
  assignmentStrategy,
  diagnosticDelay,
  completedFirstDiagnostic,
}: {
  todo: {
    onboarding: string;
    diagnostic: string;
    hasResults: boolean;
    hasHooksResults: boolean;
  };
  isDiagnosticDelayed: boolean;
  assignmentStrategy: string;
  diagnosticDelay: number;
  completedFirstDiagnostic: boolean;
}) => {
  const tasks = {
    manual: [
      ...(todo.onboarding === "requested"
        ? [
            {
              path: "/survey/onboarding",
              image: "/cards/onboarding.png",
              ...i18next.t("homePage:task.manual_strategy.onboarding", {
                returnObjects: true,
              }),
            },
          ]
        : []),

      ...(todo.diagnostic != "requested" && completedFirstDiagnostic
        ? [
            {
              path: "/survey/diagnostic",
              image: "/cards/diagnostic.png",
              ...i18next.t("homePage:task.manual_strategy.diagnostic.default", {
                returnObjects: true,
              }),
              disabled: todo.diagnostic != "requested",
            },
          ]
        : [
            {
              path: "/survey/diagnostic",
              image: "/cards/diagnostic.png",
              ...i18next.t(
                "homePage:task.manual_strategy.diagnostic.requsted",
                {
                  returnObjects: true,
                },
              ),
              disabled: todo.diagnostic != "requested",
            },
          ]),
      ...(todo.hasResults
        ? [
            {
              path: "/results/hmt",
              image: "/cards/results.png",
              ...i18next.t("homePage:task.manual_strategy.results.hasResults", {
                returnObjects: true,
              }),
              disabled: false,
            },
          ]
        : [
            {
              path: "/results/hmt",
              image: "/cards/results.png",
              ...i18next.t("homePage:task.manual_strategy.results.default", {
                returnObjects: true,
              }),
              disabled: true,
            },
          ]),

      ...(todo.hasHooksResults
        ? [
            {
              image: "/cards/results_hooks.png",
              ...i18next.t("homePage:task.manual_strategy.hooksResults", {
                returnObjects: true,
              }),

              path: "/slices",
            },
          ]
        : []),
    ],

    default: [
      ...(todo.onboarding === "requested"
        ? [
            {
              path: "/survey/onboarding",
              image: "/cards/onboarding.png",
              ...i18next.t("homePage:task.default_strategy.onboarding", {
                returnObjects: true,
              }),
            },
          ]
        : []),
      ...(isDiagnosticDelayed
        ? [
            {
              title:
                i18next.t(
                  "homePage:task.default_strategy.diagnostic.withDelay.title.day",
                  {
                    count: diagnosticDelay,
                  },
                ) + plural(diagnosticDelay, "день", "дні", "днів"),

              image: "/cards/diagnostic.png",

              description: i18next.t(
                "homePage:task.default_strategy.diagnostic.withDelay.description",
              ),
              buttonText: i18next.t(
                "homePage:task.default_strategy.diagnostic.withDelay.buttonText",
              ),
              timeDescription: i18next.t(
                "homePage:task.default_strategy.diagnostic.withDelay.timeDescription",
              ),
              path: "/survey/diagnostic",
              disabled: todo.diagnostic != "requested",
            },
          ]
        : [
            {
              path: "/survey/diagnostic",
              image: "/cards/diagnostic.png",
              ...i18next.t(
                "homePage:task.default_strategy.diagnostic.default",
                {
                  returnObjects: true,
                },
              ),
              disabled: todo.diagnostic != "requested",
            },
          ]),
      ...(todo.hasResults
        ? [
            {
              path: "/results/hmt",
              image: "/cards/results.png",

              ...i18next.t(
                "homePage:task.default_strategy.results.hasResults",
                {
                  returnObjects: true,
                },
              ),
              disabled: !todo.hasResults,
            },
          ]
        : [
            {
              path: "/results/hmt",
              image: "/cards/results.png",

              ...i18next.t("homePage:task.default_strategy.results.default", {
                returnObjects: true,
              }),
              disabled: !todo.hasResults,
            },
          ]),
      ...(todo.hasHooksResults
        ? [
            {
              image: "/cards/results_hooks.png",

              ...i18next.t("homePage:task.default_strategy.hooksResults", {
                returnObjects: true,
              }),

              path: "/slices",
            },
          ]
        : []),
    ],
  };

  return tasks[assignmentStrategy as keyof typeof tasks] as TaskData[];
};

type Task = ReturnType<typeof getTasks>[number];

export const Tasks = () => {
  const { sm } = useMedia();
  const [task] = api.home.tasksStatus.useSuspenseQuery(undefined, {
    refetchOnMount: true,
  });

  const tasks = getTasks(task);

  const initialIndex = useMemo(() => {
    return tasks.findIndex(({ disabled }) => !disabled);
  }, [tasks]);

  return (
    <XStack
      $sm={{
        flex: 1,
      }}
      flex={tasks.length}
      gap={"$2"}
      justifyContent="center"
    >
      <MatchCase value={sm}>
        {(Case) => (
          <>
            <Case condition={false}>
              {tasks.map((item, index) => (
                <Task.Container
                  width={"100%"}
                  flex={1}
                  key={index}
                  disabled={item?.disabled}
                  data-id={item.path}
                >
                  <Task.Image path={item.image} />

                  <View gap={"$3"} flex={1} justifyContent="space-between">
                    <View gap={"$0.5"}>
                      <Task.Title data-id={"task-title-" + item.path}>
                        {item.title}
                      </Task.Title>
                      <Task.Description
                        data-id={"task-description-" + item.path}
                      >
                        {item.description}
                      </Task.Description>
                    </View>

                    <View
                      height={100}
                      gap={"$1.5"}
                      $sm={{ alignItems: "center", width: "100%" }}
                    >
                      <Task.Button
                        data-id={"task-button-" + item.path}
                        onPress={() => router.push(item.path)}
                        width={"100%"}
                      >
                        {item.buttonText}
                      </Task.Button>
                      <Task.Disclaimer
                        data-id={"task-time-description-" + item.path}
                      >
                        {item.timeDescription}
                      </Task.Disclaimer>
                    </View>
                  </View>
                </Task.Container>
              ))}
            </Case>

            <Case condition={true}>
              <View width={"100%"} gap={"$3"} alignItems="center">
                <Swiper.Provider data={tasks}>
                  <Swiper.Container
                    data-id={"task-swiper-container"}
                    contentContainerStyle={{ gap: 10 }}
                    width={"100%"}
                    maxWidth={"85vw"}
                    initialIndex={initialIndex}
                  >
                    {({ item, index }: { item: Task; index: number }) => {
                      return (
                        <Swiper.Item
                          data-id={"task-swiper-item"}
                          height={445}
                          key={index}
                          paddingBottom={"$0.5"}
                        >
                          <Task.Container
                            data-id={item.path}
                            height={"100%"}
                            flex={1}
                            disabled={item?.disabled}
                          >
                            <Task.Image path={item.image} />

                            <View
                              gap={"$3"}
                              flex={1}
                              justifyContent="space-between"
                            >
                              <View gap={"$0.5"}>
                                <Task.Title data-id={"task-title"}>
                                  {item.title}
                                </Task.Title>
                                <Task.Description data-id={"task-description"}>
                                  {item.description}
                                </Task.Description>
                              </View>

                              <View
                                gap={"$1.5"}
                                $sm={{
                                  alignItems: "center",
                                  width: "100%",
                                }}
                              >
                                <Task.Button
                                  onPress={() => router.push(item.path)}
                                  width={"100%"}
                                >
                                  {item.buttonText}
                                </Task.Button>
                                <Task.Disclaimer
                                  data-id={"task-time-description"}
                                >
                                  {item?.timeDescription}{" "}
                                </Task.Disclaimer>
                              </View>
                            </View>
                          </Task.Container>
                        </Swiper.Item>
                      );
                    }}
                  </Swiper.Container>
                  <Swiper.BulletsContainer
                    data-id={"task-swiper-bullets-container"}
                    gap={"$1.5"}
                  >
                    {(index) => (
                      <Swiper.Bullet
                        key={index}
                        data-id={"task-swiper-bullet"}
                        index={index}
                        style={{
                          width: 16,
                          height: 16,
                          borderRadius: 8,
                        }}
                      />
                    )}
                  </Swiper.BulletsContainer>
                </Swiper.Provider>
              </View>
            </Case>
          </>
        )}
      </MatchCase>
    </XStack>
  );
};
